import { CommonModule } from "@angular/common";
import {
  Component,
  Input,
  AfterViewInit,
  ChangeDetectorRef,
} from "@angular/core";
import {
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  AbstractControl,
  ValidationErrors,
} from "@angular/forms";
import { CardModule } from "primeng/card";
import { CheckboxModule } from "primeng/checkbox";
import { ColorPickerChangeEvent, ColorPickerModule } from "primeng/colorpicker";
import { InputNumberModule } from "primeng/inputnumber";
import { InputTextModule } from "primeng/inputtext";
import { ButtonModule } from "primeng/button";
import { TooltipModule } from "primeng/tooltip";
import { CustomerPortalSettingsForm } from "../../../interfaces/customer-portal-settings.interface";

@Component({
  selector: "app-styling-settings",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CardModule,
    CheckboxModule,
    ColorPickerModule,
    InputNumberModule,
    InputTextModule,
    ButtonModule,
    TooltipModule,
  ],
  template: `
    <div class="styling-container">
      <!-- Layout Settings Card - Full Width Top Row -->
      <p-card class="layout-card">
        <ng-template pTemplate="title">
          <div class="flex items-center gap-2">
            <i class="pi pi-objects-column text-blue-600"></i>
            Layout Settings
            <i
              class="pi pi-info-circle text-gray-400 cursor-pointer"
              pTooltip="Configure portal layout and behavior settings"
              tooltipPosition="right"
              [style.font-size]="'0.875rem'"
            ></i>
          </div>
        </ng-template>
        <ng-template pTemplate="subtitle">
          Configure how elements are displayed and arranged in the portal.
        </ng-template>
        <ng-template pTemplate="content">
          <div class="layout-options-compact">
            <div class="option-row">
              <p-checkbox
                [formControl]="formGroup.controls.isStackedButtons"
                [binary]="true"
                inputId="stackedButtons"
              />
              <label for="stackedButtons" class="option-label-compact">
                Stack buttons
                <i
                  class="pi pi-info-circle info-icon"
                  pTooltip="Buttons on summary views will be stacked instead of scrollable"
                  tooltipPosition="top"
                ></i>
              </label>
            </div>

            <div class="option-row">
              <p-checkbox
                [formControl]="formGroup.controls.isDisplayScheduledOrderId"
                [binary]="true"
                inputId="displayOrderId"
              />
              <label for="displayOrderId" class="option-label-compact">
                Display Order ID
                <i
                  class="pi pi-info-circle info-icon"
                  pTooltip="Show Scheduled Order ID on summary and detailed views"
                  tooltipPosition="top"
                ></i>
              </label>
            </div>

            <div class="option-row">
              <p-checkbox
                [formControl]="
                  formGroup.controls.enableProductCarouselAutoSlide
                "
                [binary]="true"
                inputId="disableCarousel"
              />
              <label for="disableCarousel" class="option-label-compact">
                Disable carousel
                <i
                  class="pi pi-info-circle info-icon"
                  pTooltip="Only show first item, disable image rotation"
                  tooltipPosition="top"
                ></i>
              </label>
            </div>

            <div class="option-row">
              <p-checkbox
                [formControl]="formGroup.controls.showProductTitleOnImages"
                [binary]="true"
                inputId="hideProductTitles"
              />
              <label for="hideProductTitles" class="option-label-compact">
                Hide product titles
                <i
                  class="pi pi-info-circle info-icon"
                  pTooltip="Hide product title banners on images"
                  tooltipPosition="top"
                ></i>
              </label>
            </div>
          </div>
        </ng-template>
      </p-card>

      <!-- Color Scheme Card - Bottom Left -->
      <p-card class="color-card">
        <ng-template pTemplate="title">
          <div class="flex items-center gap-2">
            <i class="pi pi-palette text-purple-600"></i>
            Color Scheme
            <i
              class="pi pi-info-circle text-gray-400 cursor-pointer"
              pTooltip="Customize the color palette for your portal"
              tooltipPosition="right"
              [style.font-size]="'0.875rem'"
            ></i>
          </div>
        </ng-template>
        <ng-template pTemplate="subtitle">
          Define brand colors and visual themes for your customer portal.
        </ng-template>
        <ng-template pTemplate="content">
          <div class="color-groups">
            <!-- Primary Colors Group -->
            <div class="color-group">
              <div class="color-group-header">
                <span class="group-title">Primary Colors</span>
                <i
                  class="pi pi-info-circle text-gray-400"
                  pTooltip="Main brand colors for actions and highlights"
                  tooltipPosition="top"
                ></i>
              </div>
              <div class="color-pair">
                <div class="color-field-compact">
                  <label class="color-label">Primary</label>
                  <div class="color-input-group-compact">
                    <p-colorPicker
                      [ngModel]="getColorValue('primaryColor')"
                      format="hex"
                      inputId="primaryColor"
                      (onChange)="onColorPickerChange('primaryColor', $event)"
                    />
                    <input
                      pInputText
                      [formControl]="formGroup.controls.primaryColor"
                      placeholder="#000000"
                      class="color-text-input-compact"
                      [class.p-invalid]="
                        formGroup.controls.primaryColor.invalid &&
                        formGroup.controls.primaryColor.touched
                      "
                      [readonly]="!isColorFieldEditable('primaryColor')"
                      (dblclick)="enableColorInputEdit('primaryColor')"
                      (blur)="onColorInputChange('primaryColor', $event)"
                      [pTooltip]="
                        !isColorFieldEditable('primaryColor')
                          ? 'Double-click to edit'
                          : ''
                      "
                      tooltipPosition="top"
                    />
                    <p-button
                      [icon]="
                        isColorFieldEditable('primaryColor')
                          ? 'pi pi-check'
                          : 'pi pi-pencil'
                      "
                      [severity]="
                        isColorFieldEditable('primaryColor')
                          ? 'success'
                          : 'secondary'
                      "
                      [text]="true"
                      size="small"
                      (click)="toggleColorInputEdit('primaryColor')"
                    />
                  </div>
                </div>
                <div class="color-field-compact">
                  <label class="color-label">On Primary</label>
                  <div class="color-input-group-compact">
                    <p-colorPicker
                      [ngModel]="getColorValue('onPrimaryColor')"
                      format="hex"
                      inputId="onPrimaryColor"
                      (onChange)="onColorPickerChange('onPrimaryColor', $event)"
                    />
                    <input
                      pInputText
                      [formControl]="formGroup.controls.onPrimaryColor"
                      placeholder="#FFFFFF"
                      class="color-text-input-compact"
                      [class.p-invalid]="
                        formGroup.controls.onPrimaryColor.invalid &&
                        formGroup.controls.onPrimaryColor.touched
                      "
                      [readonly]="!isColorFieldEditable('onPrimaryColor')"
                      (dblclick)="enableColorInputEdit('onPrimaryColor')"
                      (blur)="onColorInputChange('onPrimaryColor', $event)"
                      [pTooltip]="
                        !isColorFieldEditable('onPrimaryColor')
                          ? 'Double-click to edit'
                          : ''
                      "
                      tooltipPosition="top"
                    />
                    <p-button
                      [icon]="
                        isColorFieldEditable('onPrimaryColor')
                          ? 'pi pi-check'
                          : 'pi pi-pencil'
                      "
                      [severity]="
                        isColorFieldEditable('onPrimaryColor')
                          ? 'success'
                          : 'secondary'
                      "
                      [text]="true"
                      size="small"
                      (click)="toggleColorInputEdit('onPrimaryColor')"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Surface Colors Group -->
            <div class="color-group">
              <div class="color-group-header">
                <span class="group-title">Surface Colors</span>
                <i
                  class="pi pi-info-circle text-gray-400"
                  pTooltip="Background colors for cards, sheets, and menus"
                  tooltipPosition="top"
                ></i>
              </div>
              <div class="color-pair">
                <div class="color-field-compact">
                  <label class="color-label">Surface</label>
                  <div class="color-input-group-compact">
                    <p-colorPicker
                      [ngModel]="getColorValue('surfaceColor')"
                      format="hex"
                      inputId="surfaceColor"
                      (onChange)="onColorPickerChange('surfaceColor', $event)"
                    />
                    <input
                      pInputText
                      [formControl]="formGroup.controls.surfaceColor"
                      placeholder="#FFFFFF"
                      class="color-text-input-compact"
                      [class.p-invalid]="
                        formGroup.controls.surfaceColor.invalid &&
                        formGroup.controls.surfaceColor.touched
                      "
                      [readonly]="!isColorFieldEditable('surfaceColor')"
                      (dblclick)="enableColorInputEdit('surfaceColor')"
                      (blur)="onColorInputChange('surfaceColor', $event)"
                      [pTooltip]="
                        !isColorFieldEditable('surfaceColor')
                          ? 'Double-click to edit'
                          : ''
                      "
                      tooltipPosition="top"
                    />
                    <p-button
                      [icon]="
                        isColorFieldEditable('surfaceColor')
                          ? 'pi pi-check'
                          : 'pi pi-pencil'
                      "
                      [severity]="
                        isColorFieldEditable('surfaceColor')
                          ? 'success'
                          : 'secondary'
                      "
                      [text]="true"
                      size="small"
                      (click)="toggleColorInputEdit('surfaceColor')"
                    />
                  </div>
                </div>
                <div class="color-field-compact">
                  <label class="color-label">On Surface</label>
                  <div class="color-input-group-compact">
                    <p-colorPicker
                      [ngModel]="getColorValue('onSurfaceColor')"
                      format="hex"
                      inputId="onSurfaceColor"
                      (onChange)="onColorPickerChange('onSurfaceColor', $event)"
                    />
                    <input
                      pInputText
                      [formControl]="formGroup.controls.onSurfaceColor"
                      placeholder="#000000"
                      class="color-text-input-compact"
                      [class.p-invalid]="
                        formGroup.controls.onSurfaceColor.invalid &&
                        formGroup.controls.onSurfaceColor.touched
                      "
                      [readonly]="!isColorFieldEditable('onSurfaceColor')"
                      (dblclick)="enableColorInputEdit('onSurfaceColor')"
                      (blur)="onColorInputChange('onSurfaceColor', $event)"
                      [pTooltip]="
                        !isColorFieldEditable('onSurfaceColor')
                          ? 'Double-click to edit'
                          : ''
                      "
                      tooltipPosition="top"
                    />
                    <p-button
                      [icon]="
                        isColorFieldEditable('onSurfaceColor')
                          ? 'pi pi-check'
                          : 'pi pi-pencil'
                      "
                      [severity]="
                        isColorFieldEditable('onSurfaceColor')
                          ? 'success'
                          : 'secondary'
                      "
                      [text]="true"
                      size="small"
                      (click)="toggleColorInputEdit('onSurfaceColor')"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Status Colors Group -->
            <div class="color-group">
              <div class="color-group-header">
                <span class="group-title">Status Colors</span>
                <i
                  class="pi pi-info-circle text-gray-400"
                  pTooltip="Colors for error states and discount indicators"
                  tooltipPosition="top"
                ></i>
              </div>
              <div class="color-pair">
                <div class="color-field-compact">
                  <label class="color-label">Error</label>
                  <div class="color-input-group-compact">
                    <p-colorPicker
                      [ngModel]="getColorValue('errorColor')"
                      format="hex"
                      inputId="errorColor"
                      (onChange)="onColorPickerChange('errorColor', $event)"
                    />
                    <input
                      pInputText
                      [formControl]="formGroup.controls.errorColor"
                      placeholder="#DC2626"
                      class="color-text-input-compact"
                      [class.p-invalid]="
                        formGroup.controls.errorColor.invalid &&
                        formGroup.controls.errorColor.touched
                      "
                      [readonly]="!isColorFieldEditable('errorColor')"
                      (dblclick)="enableColorInputEdit('errorColor')"
                      (blur)="onColorInputChange('errorColor', $event)"
                      [pTooltip]="
                        !isColorFieldEditable('errorColor')
                          ? 'Double-click to edit'
                          : ''
                      "
                      tooltipPosition="top"
                    />
                    <p-button
                      [icon]="
                        isColorFieldEditable('errorColor')
                          ? 'pi pi-check'
                          : 'pi pi-pencil'
                      "
                      [severity]="
                        isColorFieldEditable('errorColor')
                          ? 'success'
                          : 'secondary'
                      "
                      [text]="true"
                      size="small"
                      (click)="toggleColorInputEdit('errorColor')"
                    />
                  </div>
                </div>
                <div class="color-field-compact">
                  <label class="color-label">On Error</label>
                  <div class="color-input-group-compact">
                    <p-colorPicker
                      [ngModel]="getColorValue('onErrorColor')"
                      format="hex"
                      inputId="onErrorColor"
                      (onChange)="onColorPickerChange('onErrorColor', $event)"
                    />
                    <input
                      pInputText
                      [formControl]="formGroup.controls.onErrorColor"
                      placeholder="#FFFFFF"
                      class="color-text-input-compact"
                      [class.p-invalid]="
                        formGroup.controls.onErrorColor.invalid &&
                        formGroup.controls.onErrorColor.touched
                      "
                      [readonly]="!isColorFieldEditable('onErrorColor')"
                      (dblclick)="enableColorInputEdit('onErrorColor')"
                      (blur)="onColorInputChange('onErrorColor', $event)"
                      [pTooltip]="
                        !isColorFieldEditable('onErrorColor')
                          ? 'Double-click to edit'
                          : ''
                      "
                      tooltipPosition="top"
                    />
                    <p-button
                      [icon]="
                        isColorFieldEditable('onErrorColor')
                          ? 'pi pi-check'
                          : 'pi pi-pencil'
                      "
                      [severity]="
                        isColorFieldEditable('onErrorColor')
                          ? 'success'
                          : 'secondary'
                      "
                      [text]="true"
                      size="small"
                      (click)="toggleColorInputEdit('onErrorColor')"
                    />
                  </div>
                </div>
              </div>
              <div class="color-pair">
                <div class="color-field-compact">
                  <label class="color-label">Discount</label>
                  <div class="color-input-group-compact">
                    <p-colorPicker
                      [ngModel]="getColorValue('discountBarColor')"
                      format="hex"
                      inputId="discountBarColor"
                      (onChange)="
                        onColorPickerChange('discountBarColor', $event)
                      "
                    />
                    <input
                      pInputText
                      [formControl]="formGroup.controls.discountBarColor"
                      placeholder="#10B981"
                      class="color-text-input-compact"
                      [class.p-invalid]="
                        formGroup.controls.discountBarColor.invalid &&
                        formGroup.controls.discountBarColor.touched
                      "
                      [readonly]="!isColorFieldEditable('discountBarColor')"
                      (dblclick)="enableColorInputEdit('discountBarColor')"
                      (blur)="onColorInputChange('discountBarColor', $event)"
                      [pTooltip]="
                        !isColorFieldEditable('discountBarColor')
                          ? 'Double-click to edit'
                          : ''
                      "
                      tooltipPosition="top"
                    />
                    <p-button
                      [icon]="
                        isColorFieldEditable('discountBarColor')
                          ? 'pi pi-check'
                          : 'pi pi-pencil'
                      "
                      [severity]="
                        isColorFieldEditable('discountBarColor')
                          ? 'success'
                          : 'secondary'
                      "
                      [text]="true"
                      size="small"
                      (click)="toggleColorInputEdit('discountBarColor')"
                    />
                  </div>
                </div>
                <div class="color-field-compact">
                  <label class="color-label">On Discount</label>
                  <div class="color-input-group-compact">
                    <p-colorPicker
                      [ngModel]="getColorValue('onDiscountBarColor')"
                      format="hex"
                      inputId="onDiscountBarColor"
                      (onChange)="
                        onColorPickerChange('onDiscountBarColor', $event)
                      "
                    />
                    <input
                      pInputText
                      [formControl]="formGroup.controls.onDiscountBarColor"
                      placeholder="#FFFFFF"
                      class="color-text-input-compact"
                      [class.p-invalid]="
                        formGroup.controls.onDiscountBarColor.invalid &&
                        formGroup.controls.onDiscountBarColor.touched
                      "
                      [readonly]="!isColorFieldEditable('onDiscountBarColor')"
                      (dblclick)="enableColorInputEdit('onDiscountBarColor')"
                      (blur)="onColorInputChange('onDiscountBarColor', $event)"
                      [pTooltip]="
                        !isColorFieldEditable('onDiscountBarColor')
                          ? 'Double-click to edit'
                          : ''
                      "
                      tooltipPosition="top"
                    />
                    <p-button
                      [icon]="
                        isColorFieldEditable('onDiscountBarColor')
                          ? 'pi pi-check'
                          : 'pi pi-pencil'
                      "
                      [severity]="
                        isColorFieldEditable('onDiscountBarColor')
                          ? 'success'
                          : 'secondary'
                      "
                      [text]="true"
                      size="small"
                      (click)="toggleColorInputEdit('onDiscountBarColor')"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ng-template>
      </p-card>

      <!-- Button Styling & Image Dimensions Card - Bottom Right -->
      <p-card class="button-card">
        <ng-template pTemplate="title">
          <div class="flex items-center gap-2">
            <i class="pi pi-external-link text-green-600"></i>
            Button Styling & Images
            <i
              class="pi pi-info-circle text-gray-400 cursor-pointer"
              pTooltip="Customize button appearance and image dimensions"
              tooltipPosition="right"
              [style.font-size]="'0.875rem'"
            ></i>
          </div>
        </ng-template>
        <ng-template pTemplate="subtitle">
          Style interactive elements and control product image dimensions.
        </ng-template>
        <ng-template pTemplate="content">
          <div class="button-sections">
            <!-- Button Properties -->
            <div class="button-group">
              <div class="button-group-header">
                <i class="pi pi-external-link text-green-600"></i>
                <span class="group-title">Button Properties</span>
                <i
                  class="pi pi-info-circle text-gray-400"
                  pTooltip="Border thickness and corner radius settings"
                  tooltipPosition="top"
                ></i>
              </div>
              <div class="button-properties-row">
                <div class="property-field">
                  <label class="property-label">
                    Thickness
                    <i
                      class="pi pi-info-circle info-icon"
                      pTooltip="Border thickness (0-10px)"
                      tooltipPosition="top"
                    ></i>
                  </label>
                  <p-inputNumber
                    [formControl]="formGroup.controls.buttonBorderThicknessPx"
                    inputId="buttonBorderThickness"
                    [min]="0"
                    [max]="10"
                    suffix=" px"
                    [showButtons]="true"
                    buttonLayout="horizontal"
                    spinnerMode="horizontal"
                    class="compact-input"
                  />
                </div>
                <div class="property-field">
                  <label class="property-label">
                    Radius
                    <i
                      class="pi pi-info-circle info-icon"
                      pTooltip="Corner radius (0-50px)"
                      tooltipPosition="top"
                    ></i>
                  </label>
                  <p-inputNumber
                    [formControl]="formGroup.controls.buttonBorderRadiusPx"
                    inputId="buttonBorderRadius"
                    [min]="0"
                    [max]="50"
                    suffix=" px"
                    [showButtons]="true"
                    buttonLayout="horizontal"
                    spinnerMode="horizontal"
                    class="compact-input"
                  />
                </div>
              </div>
            </div>

            <!-- Button Colors -->
            <div class="button-group">
              <div class="button-group-header">
                <span class="group-title">Button Colors</span>
                <i
                  class="pi pi-info-circle text-gray-400"
                  pTooltip="Colors for button borders and states"
                  tooltipPosition="top"
                ></i>
              </div>
              <div class="color-pair">
                <div class="color-field-compact">
                  <label class="color-label">Border</label>
                  <div class="color-input-group-compact">
                    <p-colorPicker
                      [ngModel]="getColorValue('buttonBorderColor')"
                      format="hex"
                      inputId="buttonBorderColor"
                      (onChange)="
                        onColorPickerChange('buttonBorderColor', $event)
                      "
                    />
                    <input
                      pInputText
                      [formControl]="formGroup.controls.buttonBorderColor"
                      placeholder="#E5E7EB"
                      class="color-text-input-compact"
                      [class.p-invalid]="
                        formGroup.controls.buttonBorderColor.invalid &&
                        formGroup.controls.buttonBorderColor.touched
                      "
                      [readonly]="!isColorFieldEditable('buttonBorderColor')"
                      (dblclick)="enableColorInputEdit('buttonBorderColor')"
                      (blur)="onColorInputChange('buttonBorderColor', $event)"
                      [pTooltip]="
                        !isColorFieldEditable('buttonBorderColor')
                          ? 'Double-click to edit'
                          : ''
                      "
                      tooltipPosition="top"
                    />
                    <p-button
                      [icon]="
                        isColorFieldEditable('buttonBorderColor')
                          ? 'pi pi-check'
                          : 'pi pi-pencil'
                      "
                      [severity]="
                        isColorFieldEditable('buttonBorderColor')
                          ? 'success'
                          : 'secondary'
                      "
                      [text]="true"
                      size="small"
                      (click)="toggleColorInputEdit('buttonBorderColor')"
                    />
                  </div>
                </div>
                <div class="color-field-compact">
                  <label class="color-label">Selected BG</label>
                  <div class="color-input-group-compact">
                    <p-colorPicker
                      [ngModel]="getColorValue('selectedButtonBackgroundColor')"
                      format="hex"
                      inputId="selectedButtonBg"
                      (onChange)="
                        onColorPickerChange(
                          'selectedButtonBackgroundColor',
                          $event
                        )
                      "
                    />
                    <input
                      pInputText
                      [formControl]="
                        formGroup.controls.selectedButtonBackgroundColor
                      "
                      placeholder="#3B82F6"
                      class="color-text-input-compact"
                      [class.p-invalid]="
                        formGroup.controls.selectedButtonBackgroundColor
                          .invalid &&
                        formGroup.controls.selectedButtonBackgroundColor.touched
                      "
                      [readonly]="
                        !isColorFieldEditable('selectedButtonBackgroundColor')
                      "
                      (dblclick)="
                        enableColorInputEdit('selectedButtonBackgroundColor')
                      "
                      (blur)="
                        onColorInputChange(
                          'selectedButtonBackgroundColor',
                          $event
                        )
                      "
                      [pTooltip]="
                        !isColorFieldEditable('selectedButtonBackgroundColor')
                          ? 'Double-click to edit'
                          : ''
                      "
                      tooltipPosition="top"
                    />
                    <p-button
                      [icon]="
                        isColorFieldEditable('selectedButtonBackgroundColor')
                          ? 'pi pi-check'
                          : 'pi pi-pencil'
                      "
                      [severity]="
                        isColorFieldEditable('selectedButtonBackgroundColor')
                          ? 'success'
                          : 'secondary'
                      "
                      [text]="true"
                      size="small"
                      (click)="
                        toggleColorInputEdit('selectedButtonBackgroundColor')
                      "
                    />
                  </div>
                </div>
              </div>
              <div class="color-pair">
                <div class="color-field-compact">
                  <label class="color-label">Selected Text</label>
                  <div class="color-input-group-compact">
                    <p-colorPicker
                      [ngModel]="getColorValue('selectedButtonTextColor')"
                      format="hex"
                      inputId="selectedButtonText"
                      (onChange)="
                        onColorPickerChange('selectedButtonTextColor', $event)
                      "
                    />
                    <input
                      pInputText
                      [formControl]="formGroup.controls.selectedButtonTextColor"
                      placeholder="#FFFFFF"
                      class="color-text-input-compact"
                      [class.p-invalid]="
                        formGroup.controls.selectedButtonTextColor.invalid &&
                        formGroup.controls.selectedButtonTextColor.touched
                      "
                      [readonly]="
                        !isColorFieldEditable('selectedButtonTextColor')
                      "
                      (dblclick)="
                        enableColorInputEdit('selectedButtonTextColor')
                      "
                      (blur)="
                        onColorInputChange('selectedButtonTextColor', $event)
                      "
                      [pTooltip]="
                        !isColorFieldEditable('selectedButtonTextColor')
                          ? 'Double-click to edit'
                          : ''
                      "
                      tooltipPosition="top"
                    />
                    <p-button
                      [icon]="
                        isColorFieldEditable('selectedButtonTextColor')
                          ? 'pi pi-check'
                          : 'pi pi-pencil'
                      "
                      [severity]="
                        isColorFieldEditable('selectedButtonTextColor')
                          ? 'success'
                          : 'secondary'
                      "
                      [text]="true"
                      size="small"
                      (click)="toggleColorInputEdit('selectedButtonTextColor')"
                    />
                  </div>
                </div>
                <div class="color-field-compact">
                  <label class="color-label">Unselected Text</label>
                  <div class="color-input-group-compact">
                    <p-colorPicker
                      [ngModel]="getColorValue('unselectedButtonTextColor')"
                      format="hex"
                      inputId="unselectedButtonText"
                      (onChange)="
                        onColorPickerChange('unselectedButtonTextColor', $event)
                      "
                    />
                    <input
                      pInputText
                      [formControl]="
                        formGroup.controls.unselectedButtonTextColor
                      "
                      placeholder="#374151"
                      class="color-text-input-compact"
                      [class.p-invalid]="
                        formGroup.controls.unselectedButtonTextColor.invalid &&
                        formGroup.controls.unselectedButtonTextColor.touched
                      "
                      [readonly]="
                        !isColorFieldEditable('unselectedButtonTextColor')
                      "
                      (dblclick)="
                        enableColorInputEdit('unselectedButtonTextColor')
                      "
                      (blur)="
                        onColorInputChange('unselectedButtonTextColor', $event)
                      "
                      [pTooltip]="
                        !isColorFieldEditable('unselectedButtonTextColor')
                          ? 'Double-click to edit'
                          : ''
                      "
                      tooltipPosition="top"
                    />
                    <p-button
                      [icon]="
                        isColorFieldEditable('unselectedButtonTextColor')
                          ? 'pi pi-check'
                          : 'pi pi-pencil'
                      "
                      [severity]="
                        isColorFieldEditable('unselectedButtonTextColor')
                          ? 'success'
                          : 'secondary'
                      "
                      [text]="true"
                      size="small"
                      (click)="
                        toggleColorInputEdit('unselectedButtonTextColor')
                      "
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Image Dimensions Section -->
            <div class="button-group">
              <div class="button-group-header">
                <i class="pi pi-image text-orange-600"></i>
                <span class="group-title">Image Dimensions</span>
                <i
                  class="pi pi-info-circle text-gray-400"
                  pTooltip="Maximum dimensions for product images"
                  tooltipPosition="top"
                ></i>
              </div>
              <div class="button-properties-row">
                <div class="property-field">
                  <label class="property-label">
                    Max Width
                    <i
                      class="pi pi-info-circle info-icon"
                      pTooltip="Maximum image width (0-700px)"
                      tooltipPosition="top"
                    ></i>
                  </label>
                  <p-inputNumber
                    [formControl]="formGroup.controls.maxImageWidthPx"
                    inputId="maxImageWidth"
                    [min]="0"
                    [max]="700"
                    suffix=" px"
                    [showButtons]="true"
                    buttonLayout="horizontal"
                    spinnerMode="horizontal"
                    [step]="10"
                    class="compact-input"
                  />
                </div>
                <div class="property-field">
                  <label class="property-label">
                    Max Height
                    <i
                      class="pi pi-info-circle info-icon"
                      pTooltip="Maximum image height (0-700px)"
                      tooltipPosition="top"
                    ></i>
                  </label>
                  <p-inputNumber
                    [formControl]="formGroup.controls.maxImageHeightPx"
                    inputId="maxImageHeight"
                    [min]="0"
                    [max]="700"
                    suffix=" px"
                    [showButtons]="true"
                    buttonLayout="horizontal"
                    spinnerMode="horizontal"
                    [step]="10"
                    class="compact-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </ng-template>
      </p-card>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: fit-content;
        min-height: auto;
      }

      .styling-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: auto auto;
        gap: 1.5rem;
        align-items: stretch;
      }

      .layout-card {
        grid-column: 1 / -1;
        grid-row: 1;
      }

      .color-card {
        grid-column: 1;
        grid-row: 2;
      }

      .button-card {
        grid-column: 2;
        grid-row: 2;
      }

      @media (max-width: 1500px) {
        .styling-container {
          grid-template-columns: 1fr;
          grid-template-rows: auto auto auto;
          gap: 1rem;
        }

        .layout-card,
        .color-card,
        .button-card {
          grid-column: 1;
        }

        .layout-card {
          grid-row: 1;
        }

        .color-card {
          grid-row: 2;
        }

        .button-card {
          grid-row: 3;
        }
      }

      .text-muted {
        color: #6c757d;
        font-size: 0.875rem;
      }

      /* Compact Layout Options */
      .layout-options-compact {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 0.75rem;
      }

      .option-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        background-color: #f9fafb;
        transition: all 0.2s ease;
      }

      .option-row:hover {
        background-color: #f3f4f6;
        border-color: #d1d5db;
      }

      .option-label-compact {
        font-size: 0.875rem;
        font-weight: 500;
        color: #374151;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 0.25rem;
      }

      .info-icon {
        font-size: 0.75rem;
        color: #9ca3af;
        cursor: help;
      }

      .color-input-group {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .color-text-input {
        flex: 1;
      }

      .color-text-input[readonly] {
        background-color: #f8f9fa;
        cursor: not-allowed;
      }

      .number-input-group {
        width: 100%;
      }

      .number-input-group p-inputNumber {
        width: 100%;
      }

      :host ::ng-deep .p-inputnumber {
        width: 100%;
      }

      :host ::ng-deep .p-inputnumber-input {
        width: 100%;
      }

      :host ::ng-deep .p-card {
        height: 100%;
        box-shadow:
          0 1px 3px 0 rgb(0 0 0 / 0.1),
          0 1px 2px -1px rgb(0 0 0 / 0.1);
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
      }

      :host ::ng-deep .p-card .p-card-header {
        border-bottom: 1px solid #e5e7eb;
        padding: 1rem;
      }

      :host ::ng-deep .p-card .p-card-content {
        padding: 1rem;
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      :host ::ng-deep .p-card .p-card-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #111827;
      }

      :host ::ng-deep .p-card .p-card-subtitle {
        color: #6b7280;
        font-size: 0.875rem;
        margin-top: 0.25rem;
      }

      :host ::ng-deep .p-colorpicker-preview {
        border-radius: 6px;
        border: 1px solid #d1d5db;
      }

      :host ::ng-deep .p-checkbox .p-checkbox-box {
        border-radius: 4px;
      }

      :host ::ng-deep .p-invalid {
        border-color: #dc2626 !important;
        background-color: #fef2f2 !important;
      }

      :host ::ng-deep .p-invalid:focus {
        border-color: #dc2626 !important;
        box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.2) !important;
      }

      .color-field {
        margin-bottom: 1rem;
      }

      .field {
        margin-bottom: 1rem;
      }

      .space-y-3 > * + * {
        margin-top: 0.75rem;
      }

      .space-y-3 {
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      /* Color Groups Layout */
      .color-groups {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .color-group {
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 0.75rem;
        background-color: #f9fafb;
      }

      .color-group-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.75rem;
        font-size: 0.875rem;
        font-weight: 600;
        color: #374151;
      }

      .group-title {
        flex: 1;
      }

      .color-pair {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.75rem;
        margin-bottom: 0.5rem;
      }

      .color-pair:last-child {
        margin-bottom: 0;
      }

      .color-field-compact {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .color-label {
        font-size: 0.75rem;
        font-weight: 500;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.025em;
      }

      .color-input-group-compact {
        display: flex;
        align-items: center;
        gap: 0.25rem;
      }

      .color-text-input-compact {
        flex: 1;
        font-size: 0.75rem;
        padding: 0.25rem 0.5rem;
        border: 1px solid #d1d5db;
        border-radius: 4px;
        min-width: 0;
      }

      .color-text-input-compact[readonly] {
        background-color: #f3f4f6;
        cursor: not-allowed;
      }

      /* Button Sections Layout */
      .button-sections {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .button-group {
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 0.75rem;
        background-color: #f9fafb;
      }

      .button-group-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.75rem;
        font-size: 0.875rem;
        font-weight: 600;
        color: #374151;
      }

      .button-properties-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.75rem;
      }

      .property-field {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .property-label {
        font-size: 0.75rem;
        font-weight: 500;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.025em;
        display: flex;
        align-items: center;
        gap: 0.25rem;
      }

      .compact-input {
        font-size: 0.875rem;
      }

      :host ::ng-deep .compact-input .p-inputnumber-input {
        padding: 0.375rem 0.5rem;
        font-size: 0.875rem;
      }

      :host ::ng-deep .compact-input .p-button {
        padding: 0.25rem;
      }

      /* Medium screen improvements */
      @media (max-width: 968px) {
        .color-pair {
          grid-template-columns: 1fr;
          gap: 1rem;
        }

        .button-properties-row {
          grid-template-columns: 1fr;
          gap: 1rem;
        }

        .color-input-group-compact {
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .color-text-input-compact {
          min-width: 120px;
        }
      }

      @media (max-width: 768px) {
        .layout-options-compact {
          grid-template-columns: 1fr;
        }

        .color-pair {
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }

        .button-properties-row {
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }

        .color-input-group-compact {
          gap: 0.375rem;
        }
      }

      :host ::ng-deep .p-colorpicker-preview {
        width: 24px;
        height: 24px;
        border-radius: 4px;
        border: 1px solid #d1d5db;
      }

      :host ::ng-deep .p-button.p-button-sm {
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem;
      }
    `,
  ],
})
export class StylingSettingsComponent implements AfterViewInit {
  @Input({ required: true }) formGroup!: FormGroup<CustomerPortalSettingsForm>;
  @Input({ required: true }) isLoading!: boolean;

  editableColorFields = new Set<string>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    // Force initial change detection to ensure proper sync
    this.cdr.detectChanges();
  }

  toggleColorInputEdit(fieldName: string): void {
    if (this.editableColorFields.has(fieldName)) {
      this.editableColorFields.delete(fieldName);
    } else {
      this.editableColorFields.add(fieldName);
    }
  }

  /**
   * Enable edit mode on double-click (does not toggle, only enables)
   */
  enableColorInputEdit(fieldName: string): void {
    this.editableColorFields.add(fieldName);
  }

  isColorFieldEditable(fieldName: string): boolean {
    return this.editableColorFields.has(fieldName);
  }

  getColorValue(fieldName: string): string {
    const control = this.formGroup.get(fieldName);
    return (control?.value as string) || "#000000";
  }

  onColorPickerChange(fieldName: string, event: ColorPickerChangeEvent): void {
    const newColor = typeof event === "string" ? event : event.value || "";
    if (
      newColor &&
      typeof newColor === "string" &&
      this.isValidHexColor(newColor)
    ) {
      const control = this.formGroup.get(fieldName);
      if (control) {
        control.setValue(newColor);
        control.markAsTouched();
        this.cdr.detectChanges();
      }
    }
  }

  onColorInputChange(fieldName: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const newColor = target.value;
    const control = this.formGroup.get(fieldName);
    if (control && control.value !== newColor) {
      control.setValue(newColor);
      control.markAsTouched();
      this.cdr.detectChanges();
    }
  }

  private isValidHexColor(color: string): boolean {
    return /^#([A-Fa-f0-9]{6})$/.test(color) && color.length === 7;
  }

  // Custom validator for hex color that requires exactly 7 characters
  private hexColorValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return { required: true };
    }
    const value = control.value as string;
    const valid = /^#([A-Fa-f0-9]{6})$/.test(value) && value.length === 7;
    return valid ? null : { invalidHexColor: true };
  }
}
